/**
 * Product tour — 5 lightweight steps.
 *
 * Designed to be fast (<30 seconds), zero page navigation.
 * All steps target elements on the agent page (post-onboarding landing).
 */

import {
  navigateToTourPage,
  TOUR_PAGE_PATHS,
} from './tourNavigation';

// ─── Step type ────────────────────────────────────────────────────────────────

export interface TourStep {
  id: string;
  target: string;
  title: string;
  content: string;
  placement?: 'top' | 'bottom' | 'left' | 'right';
  className?: string;
  onEnter?: () => Promise<void>;
}

// ─── Constants ────────────────────────────────────────────────────────────────

export const TOUR_ID             = 'product-tour';
export const TOUR_CENTERED_CLASS = 'tour-step-centered';
export const TOUR_OPEN_MOBILE_NAV     = 'ggj-tour-open-mobile-nav';
export const FORCE_PRODUCT_TOUR_EVENT = 'ggj:force-product-tour';

async function wait(ms = 120): Promise<void> {
  await new Promise<void>((r) => window.setTimeout(r, ms));
}

// ─── Steps (5 total — fast, no page hops) ─────────────────────────────────────

export const TOUR_STEPS: TourStep[] = [

  // 1. Welcome — centred modal
  {
    id:        'welcome',
    target:    'body',
    title:     'Welcome to GoGrabJob!',
    content:   'Your AI-powered career workspace. We match jobs to your profile, tailor your resume, and generate cover letters — all in one place. Let\'s take a quick look.',
    placement: 'bottom',
    className: TOUR_CENTERED_CLASS,
  },

  // 2. Credits badge
  {
    id:        'credits',
    target:    '[data-tour="credits-display"]',
    title:     'Your Credits',
    content:   'You start with 5 free credits. Each resume optimization costs 3 credits, each letter costs 2. Upgrade anytime for unlimited access.',
    placement: 'bottom',
    async onEnter() {
      await wait(200);
    },
  },

  // 3. Navigation sidebar
  {
    id:        'nav-sidebar',
    target:    '[data-tour="sidebar-navigation"]',
    title:     'Navigation',
    content:   'Jump between Home, Jobs, AI Agent, and Activity tracking. The AI Agent is your main workspace — it\'s where the magic happens.',
    placement: 'right',
    async onEnter() {
      await wait(200);
    },
  },

  // 4. Agent composer (the key feature)
  {
    id:        'agent-composer',
    target:    '[data-tour="agent-composer"]',
    title:     'AI Agent — Your Career Assistant',
    content:   'Type anything here — "Find React jobs", paste a job URL to optimize your resume, or ask for a cover letter. The agent handles it all.',
    placement: 'top',
    async onEnter() {
      await navigateToTourPage('agent', '[data-tour="agent-composer"]');
    },
  },

  // 5. Finale
  {
    id:        'finale',
    target:    'body',
    title:     'You\'re all set! 🎉',
    content:   'Start by typing "Find jobs for me" or upload a job URL to optimize your resume. Your 5 free credits are ready to use.',
    placement: 'bottom',
    className: TOUR_CENTERED_CLASS,
  },

];

// Re-export for legacy imports.
export { TOUR_PAGE_PATHS as PAGE_PATHS };
